"use client";

import React, { useMemo, useState } from "react";
import { Check, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const pricingPlans = [{
  name: "Starter",
  label: "",
  supportLine: "Pilot a controlled RAG deployment",
  description: "Best for pilots and small teams.",
  monthlyPrice: 149,
  annualPrice: 127,
  tenants: "1 tenant",
  seats: "5 seats",
  caps: ["0.5GB storage", "100k vectors", "10k queries / month", "30k writes / month"],
  features: ["Draft → Review → Approved governance", "Citation-based answers", "Refusal when evidence is insufficient", "Audit log export", "Tenant isolation", "BYO-Key support"],
  overLimit: "Blocked until upgrade",
  ctaText: "Start Starter",
  ctaLink: "/pricing",
  isPopular: false,
  highlight: false
}, {
  name: "Business",
  label: "Most Popular",
  supportLine: "Run business knowledge workflows with control",
  description: "Best for growing teams and smaller agency operations.",
  monthlyPrice: 399,
  annualPrice: 339,
  tenants: "3 tenants",
  seats: "20 seats",
  caps: ["2GB storage", "250k vectors", "30k queries / month", "100k writes / month"],
  features: ["Draft → Review → Approved governance", "Citation-based answers", "Refusal when evidence is insufficient", "Audit log export", "Tenant isolation", "BYO-Key support", "White-label eligible"],
  overLimit: "Blocked until upgrade",
  ctaText: "Start Business",
  ctaLink: "/pricing",
  isPopular: true,
  highlight: true
}, {
  name: "Agency",
  label: "Best for Resellers",
  supportLine: "Deliver client-ready RAG at scale",
  description: "Best for reselling and high-volume multi-tenant usage.",
  monthlyPrice: 999,
  annualPrice: 849,
  tenants: "10 tenants",
  seats: "50 seats",
  caps: ["10GB storage", "2.5M vectors", "300k queries / month", "1M writes / month"],
  features: ["Draft → Review → Approved governance", "Citation-based answers", "Refusal when evidence is insufficient", "Audit log export", "Tenant isolation", "BYO-Key support", "White-label eligible"],
  overLimit: "Blocked until upgrade",
  ctaText: "Start Agency",
  ctaLink: "/pricing",
  isPopular: false,
  highlight: false
}, {
  name: "Enterprise",
  label: "Sales-led",
  supportLine: "Tailored scale, support, and deployment design",
  description: "For larger deployments and custom requirements.",
  monthlyPrice: null,
  annualPrice: null,
  customPriceLabel: "From $2,499",
  tenants: "Custom tenant scale",
  seats: "Custom seat volume",
  caps: ["Tier 4+ capacity options", "Dedicated deployment options", "Advanced onboarding", "Advanced support"],
  features: ["Draft → Review → Approved governance", "Citation-based answers", "Refusal when evidence is insufficient", "Audit log export", "Tenant isolation", "BYO-Key support", "Sales-led plan design"],
  overLimit: "Custom scaling path",
  ctaText: "Talk to Sales",
  ctaLink: "/book-a-demo",
  isPopular: false,
  highlight: false
}];
function formatUsd(value: number) {
  return `$${value}`;
}
export default function TransparentSubscriptionPlans() {
  const [isAnnual, setIsAnnual] = useState(false);
  const annualSavingsText = useMemo(() => "Annual billing saves 15%", []);
  return <section id="transparent-subscription-plans" className="scroll-mt-32 bg-background py-14 text-foreground lg:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Transparent subscription plans
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            EloRAG subscriptions use hard backend-enforced caps. No unlimited usage,
            no bundled model credits, and no unclear blending of platform fees with AI usage.
          </p>

          <div className="mt-7 inline-flex items-center gap-4 rounded-full border border-border bg-muted/30 px-4 py-2">
            <span className={`text-sm font-medium ${!isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Monthly
            </span>

            <Switch checked={isAnnual} onCheckedChange={setIsAnnual} aria-label="Toggle annual billing" />

            <span className={`flex items-center gap-2 text-sm font-medium ${isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Annual
              <Badge variant="secondary" className="border-primary/20 bg-primary/10 text-primary hover:bg-primary/15">
                Save 15%
              </Badge>
            </span>
          </div>

          <p className="mt-3 text-sm text-muted-foreground">{annualSavingsText}</p>
        </div>

        <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 xl:grid-cols-4">
          {pricingPlans.map((plan, i) => {
          const displayedPrice = plan.monthlyPrice !== null ? isAnnual ? formatUsd(plan.annualPrice as number) : formatUsd(plan.monthlyPrice as number) : plan.customPriceLabel;
          return <div key={plan.name} data-index={i} className="relative h-full">
                {plan.label && <div className="absolute left-0 right-0 -top-4 z-10 flex justify-center">
                    <Badge className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider shadow-sm ${plan.isPopular ? "bg-primary text-primary-foreground" : "border border-border bg-secondary text-secondary-foreground"}`}>
                      {plan.label}
                    </Badge>
                  </div>}

                <Card className={`flex h-full min-h-[820px] flex-col rounded-2xl border transition-all duration-200 ${plan.highlight ? "border-primary ring-2 ring-primary/15 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.14)]" : "border-border/70 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)]"}`}>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl text-foreground">{plan.name}</CardTitle>

                    <div className="mt-2 min-h-[36px] text-sm font-medium text-primary">
                      {plan.supportLine}
                    </div>

                    <CardDescription className="mt-2 min-h-[44px] text-sm leading-relaxed">
                      {plan.description}
                    </CardDescription>

                    <div className="mt-5 min-h-[64px]">
                      <div className="flex items-end gap-2">
                        <div className="text-4xl font-bold tracking-tight text-foreground">
                          {displayedPrice}
                        </div>
                        {plan.monthlyPrice !== null && <span className="pb-1 text-sm font-medium text-muted-foreground">/mo</span>}
                      </div>

                      {plan.monthlyPrice !== null && isAnnual && <p className="mt-2 text-xs text-muted-foreground">
                          Billed annually at {formatUsd((plan.annualPrice as number) * 12)}/year
                        </p>}

                      {plan.monthlyPrice !== null && !isAnnual && <p className="mt-2 text-xs text-muted-foreground">Billed monthly</p>}

                      {plan.monthlyPrice === null && <p className="mt-2 text-xs text-muted-foreground">Sales-led pricing</p>}
                    </div>
                  </CardHeader>

                  <CardContent className="flex flex-1 flex-col space-y-5">
                    <div className="min-h-[205px] rounded-xl border border-border/70 bg-secondary/25 p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Info className="size-4 text-muted-foreground" />
                        Plan Capacity
                      </div>

                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="font-medium text-foreground">{plan.tenants}</div>
                        <div className="font-medium text-foreground">{plan.seats}</div>

                        <ul className="mt-3 space-y-2">
                          {plan.caps.map((cap, idx) => <li key={idx}>{cap}</li>)}
                        </ul>
                      </div>
                    </div>

                    <div className="flex min-h-[235px] flex-col">
                      <div className="mb-3 text-sm font-semibold text-foreground">
                        Included Features
                      </div>

                      <ul className="space-y-2">
                        {plan.features.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-2 text-sm">
                            <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                            <span className="leading-relaxed text-muted-foreground">{feature}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="mt-auto rounded-lg border border-border/60 bg-background p-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Over-limit behavior
                      </div>
                      <div className="mt-1 text-sm font-medium text-foreground">{plan.overLimit}</div>
                    </div>
                  </CardContent>

                  <CardFooter className="pb-6 pt-3">
                    <Button asChild variant={plan.highlight ? "default" : "outline"} className="h-12 w-full text-base" size="lg">
                      <Link to={plan.ctaLink}>{plan.ctaText}</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-10 rounded-xl border border-border/70 bg-secondary/25 p-4 text-center">
          <p className="flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground">
            <Info className="size-4" />
            Need more capacity? Monthly
            <Link to="/capacity-packs" className="font-medium text-primary hover:underline">
              Capacity Packs
            </Link>
            are available for scaling storage, vectors, and queries.
          </p>
        </div>
      </div>
    </section>;
}